import { useEffect, useState } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import './App.css';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';

function App() {
  const isBrowserLocalhost =
    typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const apiBaseUrl = (
    process.env.REACT_APP_API_BASE_URL ||
    (process.env.NODE_ENV === 'development' && isBrowserLocalhost ? 'http://localhost:8787' : '')
  ).replace(/\/+$/, '');
  const [theme, setTheme] = useState('dark');
  const [isTopbarCompact, setIsTopbarCompact] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState({ loading: false, message: '', error: false });

  useEffect(() => {
    const savedTheme = localStorage.getItem('vekac-theme');
    const initialTheme = savedTheme || 'dark';
    setTheme(initialTheme);
    document.documentElement.setAttribute('data-theme', initialTheme);
  }, []);

  useEffect(() => {
    const updateParallax = () => {
      document.documentElement.style.setProperty('--scroll-y', `${window.scrollY}px`);
      setIsTopbarCompact(window.scrollY > 28);
    };

    updateParallax();
    window.addEventListener('scroll', updateParallax, { passive: true });
    return () => window.removeEventListener('scroll', updateParallax);
  }, []);

  const toggleTheme = () => {
    const nextTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(nextTheme);
    localStorage.setItem('vekac-theme', nextTheme);
    document.documentElement.setAttribute('data-theme', nextTheme);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormStatus({ loading: true, message: '', error: false });

    try {
      const response = await fetch(`${apiBaseUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Unable to send message right now.');
      }

      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      setFormStatus({ loading: false, message: 'Your message has been sent successfully.', error: false });
    } catch (error) {
      setFormStatus({ loading: false, message: error.message, error: true });
    }
  };

  return (
    <div className="site">
      <div className="ambient ambient-one" />
      <div className="ambient ambient-two" />

      <header className={`topbar${isTopbarCompact ? ' is-compact' : ''}`}>
        <a className="brand" href="/" >
          <img
            src={isTopbarCompact ? '/images/VekacLogoSmall.png' : '/images/VekacTransparentLogo.png'}
            alt="VEKAC SUPPLY LLC"
            loading="eager"
            width={isTopbarCompact ? '50px' : '200px'}
            style={{ display: 'flex', alignItems: 'center', borderRadius: '8px', padding: '4px', backgroundColor: theme==='dark' ? 'rgba(255, 255, 255, 0.9)' : 'transparent' }}
          />
        </a>
        <nav className="nav">
          <a href="/#about">About</a>
          <a href="/#segments">Segments</a>
          <a href="/#services">Services</a>
          <a href="/#contact">Contact</a>
          <Link to="/privacy-policy">Privacy</Link>
          <Link to="/terms-of-service">Terms</Link>
        </nav>
        <button
          type="button"
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        >
          <span aria-hidden="true" className="theme-icon">
            {theme === 'light' ? '🌙' : '☀️'}
          </span>
        </button>
      </header>

      <main className="page-main">
        <Routes>
          <Route
            path="/"
            element={
              <>
                <section id="welcome" className="hero page-section section-odd">
                  <div className="hero-bg" aria-hidden="true">
                    <img src="/images/Globe.png" alt="" loading="lazy" />
                  </div>
                  <div className="section-inner">
                    <p className="eyebrow">Welcome to VEKAC SUPPLY LLC</p>
                    <h1 className="hero-title">VEKAC SUPPLY LLC</h1>
                    <p>
                      We are a leading provider of consulting services serving diverse market segments across
                      the United States. Headquartered in Orlando, Florida.
                    </p>
                  </div>
                </section>

                <section id="about" className="page-section section-even">
                  <div className="section-inner about-grid">
                    <div className="about-copy">
                      <h2>About Us</h2>
                      <p>
                        Founded in 2026, <strong>VEKAC SUPPLY LLC</strong> has quickly established itself as a
                        trusted consulting partner for businesses seeking to improve their operational
                        performance. We help organizations optimize their supply chain, procurement, logistics,
                        warehousing, and business processes through practical, results-driven solutions. Serving
                        clients across the United States and Latin America, we work closely with companies to
                        increase efficiency, reduce costs, and support sustainable growth.
                      </p>
                    </div>
                    <figure className="section-media about-media">
                      <img
                        src="/images/Warehouse.png"
                        alt="Warehouse operations and inventory management"
                        loading="lazy"
                      />
                    </figure>
                  </div>
                </section>

                <section id="segments" className="page-section section-odd">
                  <div className="section-inner segments-grid">
                    <figure className="section-media segments-media">
                      <img
                        src="/images/Plane.png"
                        alt="Air cargo and international freight movement"
                        loading="lazy"
                      />
                    </figure>
                    <div className="segments-copy">
                      <h2>Segments</h2>
                      <p>
                        Comprehensive consulting solutions for business operations. We help organizations
                        optimize their supply chain, procurement, logistics, warehousing, and operational
                        processes to improve efficiency, reduce costs, and drive sustainable growth.
                      </p>
                    </div>
                  </div>
                </section>

                <section id="services" className="page-section section-even services">
                  <div className="services-bg" aria-hidden="true">
                    <img src="/images/Port.png" alt="" loading="lazy" />
                  </div>
                  <div className="section-inner">
                    <h2>Services</h2>
                    <p>
                      We provide comprehensive consulting services to help businesses optimize their operations
                      and improve overall performance. Our expertise includes:
                    </p>
                    <ul className="two-col-list">
                      <li>Supply Chain Management</li>
                      <li>Logistics and Distribution</li>
                      <li>Procurement and Strategic Sourcing</li>
                      <li>Warehouse Management</li>
                      <li>Demand Planning and Sales Forecasting</li>
                      <li>Inventory Management and Stock Optimization</li>
                      <li>Material Requirements Planning (MRP)</li>
                      <li>Supplier Development and Performance Evaluation</li>
                      <li>Strategic Supplier Negotiation</li>
                      <li>International Trade (Import and Export Operations)</li>
                      <li>Transportation Management</li>
                      <li>Logistics Cost Optimization</li>
                      <li>Business Process Design and Improvement</li>
                      <li>Key Performance Indicators (KPI) Development and Implementation</li>
                      <li>Continuous Improvement Projects</li>
                      <li>Lean Manufacturing and Lean Logistics</li>
                      <li>Supply Chain Cost Analysis and Profitability Improvement</li>
                      <li>Supply Chain Risk Management</li>
                      <li>Sales and Operations Planning (S&amp;OP)</li>
                      <li>ERP Implementation of Reports</li>
                      <li>Business Process Automation</li>
                      <li>Warehouse and Inventory Audits</li>
                      <li>Warehouse Layout Design and Optimization</li>
                      <li>Customs Compliance and International Trade Documentation</li>
                    </ul>
                  </div>
                </section>

                <section id="contact" className="page-section section-odd">
                  <div className="section-inner contact-grid">
                    <div>
                      <h2>Contact</h2>
                      <ul className="contact-list">
                        <li>7345 W SAND LAKE RD STE 210 OFFICE 4975 ORLANDO, FLORIDA</li>
                        <li>
                          <a href="mailto:sales@vekac-supply.com">sales@vekac-supply.com</a>
                        </li>
                        <li>Monday to Friday - 08:00-19:00</li>
                      </ul>
                    </div>

                    <form className="contact-form" onSubmit={handleSubmit}>
                      <h3>Send Us a Message</h3>
                      <label htmlFor="name">Name</label>
                      <input id="name" name="name" value={formData.name} onChange={handleChange} required />

                      <label htmlFor="email">Email</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />

                      <label htmlFor="phone">Phone</label>
                      <input id="phone" name="phone" value={formData.phone} onChange={handleChange} />

                      <label htmlFor="company">Company</label>
                      <input id="company" name="company" value={formData.company} onChange={handleChange} />

                      <label htmlFor="message">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        rows="5"
                        value={formData.message}
                        onChange={handleChange}
                        required
                      />

                      <button type="submit" disabled={formStatus.loading}>
                        {formStatus.loading ? 'Sending...' : 'Send Message'}
                      </button>
                      {formStatus.message && (
                        <p className={formStatus.error ? 'status error' : 'status success'}>
                          {formStatus.message}
                        </p>
                      )}
                    </form>
                  </div>
                </section>
              </>
            }
          />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        </Routes>
      </main>

      <footer className="footer">
        <p>© {new Date().getFullYear()} VEKAC SUPPLY LLC. All rights reserved.</p>
        <p>
          <Link to="/privacy-policy">Privacy Policy</Link> |{' '}
          <Link to="/terms-of-service">Terms of Service</Link>
        </p>
      </footer>
    </div>
  );
}

export default App;
